import React, { Component, useRef, useState, useEffect } from "react";
import { push } from "connected-react-router";
import { Helmet } from "react-helmet";
import { connect } from "react-redux";
import { Icon, Table } from "components";
import { setBreadcrumb, setLoader, setTitle } from "store/actions";
import { API, Endpoints } from "utils/api";
import { notification, Drawer, Row, Col, Input, Select, Tooltip } from "antd";
import Strings from "utils/strings";
import { LANGUAGES, translate } from "utils/utils";

function PartNameCell({ value }: { value: string }) {
	const ref = useRef<HTMLSpanElement>(null);
	const [truncated, setTruncated] = useState(false);

	useEffect(() => {
		const el = ref.current;
		if (!el) return;
		setTruncated(el.scrollWidth > el.clientWidth);
	}, [value]);

	const content = (
		<span
			ref={ref}
			style={{
				display: "block",
				overflow: "hidden",
				textOverflow: "ellipsis",
				whiteSpace: "nowrap"
			}}
		>
			{value || "-"}
		</span>
	);

	return truncated ? (
		<Tooltip title={value || "-"} placement="topLeft">
			{content}
		</Tooltip>
	) : (
		content
	);
}

class WorkshopParts extends Component<any, any> {
	constructor(props: any) {
		super(props);

		this.state = {
			parts: [],
			showDrawer: false,
			tempPart: null,
			language: "pt"
		};
	}

	componentDidMount() {
		const { dispatch } = this.props;

		dispatch(setTitle(""));
		dispatch(setBreadcrumb(null));

		this.getData();
	}

	async getData() {
		const { dispatch } = this.props;

		dispatch(setLoader(true));

		try {
			const response = await API.get({
				url: Endpoints.uriWorkshopParts()
			});

			if (response.ok) {
				this.setState({ parts: response.data?.results?.part || [] });
			}
		} catch (err) {
			console.log("API Error", err);
			notification.error({
				message: Strings.serverErrors.title,
				description: (err as string) || Strings.serverErrors.wentWrong,
				placement: "bottomRight",
				duration: 5
			});
		}

		dispatch(setLoader(false));
	}


	async removePart(id: string) {
		const { dispatch } = this.props;

		dispatch(setLoader(true));

		try {
			const response = await API.delete({
				url: Endpoints.uriWorkshopParts(id)
			});

			if (response.ok) {
				this.setState({ parts: response.data?.results?.part || [] });

				notification.success({
					message: Strings.workshop.part,
					description: response.data.message || "",
					placement: "bottomRight",
					duration: 5
				});
			} else {
				notification.error({
					message: Strings.serverErrors.title,
					description: response?.data?.message || Strings.serverErrors.wentWrong,
					placement: "bottomRight",
					duration: 5
				});
			}
		} catch (err) {
			notification.error({
				message: Strings.serverErrors.title,
				description: (err as string) || Strings.serverErrors.wentWrong,
				placement: "bottomRight",
				duration: 5
			});
		}

		dispatch(setLoader(false));
	}

	async togglePart(part: any) {
		const { dispatch } = this.props;

		dispatch(setLoader(true));

		try {
			const response = await API.patch({
				url: Endpoints.uriWorkshopParts(`${part?._id}/status`),
				data: { isActive: !part.isActive }
			});

			if (response.ok) {
				this.setState({ parts: response.data?.results?.part || [] });

				notification.success({
					message: Strings.workshop.part,
					description: response.data.message || "",
					placement: "bottomRight",
					duration: 5
				});
			} else {
				notification.error({
					message: Strings.serverErrors.title,
					description: response?.data?.message || Strings.serverErrors.wentWrong,
					placement: "bottomRight",
					duration: 5
				});
			}
		} catch (err) {
			notification.error({
				message: Strings.serverErrors.title,
				description: (err as string) || Strings.serverErrors.wentWrong,
				placement: "bottomRight",
				duration: 5
			});
		}

		dispatch(setLoader(false));
	}

	async submitPart() {
		const { tempPart } = this.state;
		const { dispatch } = this.props;

		if (!translate(tempPart?.name)) {
			return notification.warn({
				message: Strings.workshop.part,
				description: Strings.errors.invalidFields,
				placement: "bottomRight",
				duration: 5
			});
		}

		dispatch(setLoader(true));

		try {
			const request = tempPart._id ? API.put : API.post;

			const response = await request({
				url: Endpoints.uriWorkshopParts(tempPart._id || ""),
				data: { 
					...tempPart,
					partNumber: tempPart.partNumber?.toUpperCase() || ""
				}
			});

			if (response.ok && response.data.results?.part) {
				this.setState({
					showDrawer: false,
					tempPart: null
				});

				await this.getData();

				notification.success({
					message: Strings.workshop.part,
					description: response.data.message || "",
					placement: "bottomRight",
					duration: 5
				});
			} else {
				notification.error({
					message: Strings.serverErrors.title,
					description: response?.data?.message || Strings.serverErrors.wentWrong,
					placement: "bottomRight",
					duration: 5
				});
			}
		} catch (err) {
			notification.error({
				message: Strings.serverErrors.title,
				description: (err as string) || Strings.serverErrors.wentWrong,
				placement: "bottomRight",
				duration: 5
			});
		}

		dispatch(setLoader(false));
	}

	async recalculateStock() {
		const { dispatch } = this.props;

		dispatch(setLoader(true));

		try {
			const response = await API.patch({
				url: Endpoints.uriPartsRecalculateStock()
			});

			if (response.ok) {
				await this.getData();
				notification.success({
					message: Strings.workshop.part,
					description: response.data?.message || "",
					placement: "bottomRight",
					duration: 5
				});
			} else {
				notification.error({
					message: Strings.serverErrors.title,
					description: response?.data?.message || Strings.serverErrors.wentWrong,
					placement: "bottomRight",
					duration: 5
				});
			}
		} catch (err) {
			notification.error({
				message: Strings.serverErrors.title,
				description: (err as string) || Strings.serverErrors.wentWrong,
				placement: "bottomRight",
				duration: 5
			});
		}

		dispatch(setLoader(false));
	}

	renderDrawer() {
		const { showDrawer } = this.state;

		return (
			<Drawer
				title={
					<div className="SidebarTitleContainer">
						<Icon name="tools" />
						<p>{Strings.workshop.part}</p>
					</div>
				}
				footer={
					<div className="SidebarFooterContainer">
						<button
							type="button"
							className="SidebarFooterButton --button-confirm"
							onClick={() => this.submitPart()}
						>
							{Strings.generic.confirm}
						</button>
						<button
							type="button"
							className="SidebarFooterButton --button-cancel"
							onClick={() => this.setState({ showDrawer: false, tempPart: null })}
						>
							{Strings.generic.cancel}
						</button>
					</div>
				}
				placement="right"
				width={400}
				onClose={() => this.setState({ tempPart: null, showDrawer: false })}
				visible={showDrawer}
			>
				{this.renderDrawerContent()}
			</Drawer>
		);
	}

	renderDrawerContent() {
		const { tempPart, language } = this.state;

		const unitOptions = [
			{ value: 'un', label: 'Unidade (un)' },
			{ value: 'l', label: 'Litro (l)' },
			{ value: 'kg', label: 'Quilograma (kg)' },
			{ value: 'm', label: 'Metro (m)' },
			{ value: 'm2', label: 'Metro Quadrado (m²)' },
			{ value: 'ml', label: 'Metro Linear (ml)' }
		];

		return (
			<React.Fragment>
				<div className="DrawerLanguageContainer">
					<Select
						className="DrawerLanguageDropdown"
						optionFilterProp="children"
						onChange={(language) => this.setState({ language })}
						filterOption={(input: any, option: any) =>
							option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
						}
						value={language}
					>
						{LANGUAGES.map((lang: any, index: number) => {
							return (
								<Select.Option key={`select_option_${lang}_${index}`} value={lang.value}>
									{lang.label}
								</Select.Option>
							);
						})}
					</Select>
				</div>
				<Row gutter={[0, 10]}>
					<Col xs={24}>
						<label htmlFor="part_number" className="InputLabel --label-required">
							{Strings.workshop.partNumber}
						</label>
						<Input
							id="part_number"
							value={tempPart?.partNumber || ""}
							placeholder={Strings.workshop.partNumber}
							onChange={(event: any) => {
								const value = event.target.value;
								this.setState((state: any) => ({
									tempPart: {
										...state.tempPart,
										partNumber: value
									}
								}));
							}}
							onBlur={(event: any) => {
								const value = event.target.value?.toUpperCase() || "";
								this.setState((state: any) => ({
									tempPart: {
										...state.tempPart,
										partNumber: value
									}
								}));
							}}
						/>
					</Col>
					<Col xs={24}>
						<label htmlFor="part_name" className="InputLabel --label-required">
							{Strings.workshop.name}
						</label>
						<Input
							id="part_name"
							value={tempPart?.name?.[language] || ""}
							placeholder={Strings.workshop.name}
							onChange={(event: any) => {
								const value = event.target.value;
								this.setState((state: any) => ({
									tempPart: {
										...state.tempPart,
										name: { ...state.tempPart?.name, [language]: value }
									}
								}));
							}}
						/>
					</Col>
					<Col xs={24}>
						<label htmlFor="part_instructions" className="InputLabel">
							{Strings.workshop.instructions}
						</label>
						<Input.TextArea
							id="part_instructions"
							value={tempPart?.instructions?.[language] || ""}
							placeholder={Strings.workshop.instructions}
							rows={4}
							onChange={(event: any) => {
								const value = event.target.value;
								this.setState((state: any) => ({
									tempPart: {
										...state.tempPart,
										instructions: { ...state.tempPart?.instructions, [language]: value }
									}
								}));
							}}
						/>
					</Col>
					<Col xs={24}>
						<label htmlFor="part_unit" className="InputLabel --label-required">
							{Strings.workshop.unit}
						</label>
						<Select
							id="part_unit"
							className="tagsSelector"
							style={{ width: "100%" }}
							placeholder={Strings.workshop.unit}
							value={tempPart?.unit || 'un'}
							onChange={(value: any) => {
								this.setState((state: any) => ({
									tempPart: {
										...state.tempPart,
										unit: value
									}
								}));
							}}
						>
							{unitOptions.map((option) => (
								<Select.Option key={option.value} value={option.value}>
									{option.label}
								</Select.Option>
							))}
						</Select>
					</Col>
				</Row>
			</React.Fragment>
		);
	}

	render() {
		const { parts = [] } = this.state;

		return (
			<React.Fragment>
				<Helmet>
					<title>{Strings.workshop.parts}</title>
					<meta name="description" content="Description of Workshop Parts" />
				</Helmet>
				<Table
					title={{
						icon: "tools",
						title: Strings.workshop.parts
					}}
					headerOptions={[{
						label: Strings.workshop.recalculateStock,
						tooltip: Strings.workshop.recalculateStock,
						icon: "refresh",
						onClick: () => this.recalculateStock()
					}]}
					data={parts}
					columns={[
						{
							Header: Strings.workshop.partNumber,
							id: "partNumber",
							accessor: (row: any) => row.partNumber || "-"
						},
						{
							Header: Strings.workshop.name,
							id: "name",
							width: 300,
							accessor: (row: any) => translate(row.name) || "-",
							Cell: ({ value }: { value: string }) => <PartNameCell value={value} />
						},
						{
							Header: Strings.workshop.quantity,
							id: "quantity",
							width: 50,
							accessor: (row: any) => row.quantity || 0
						},
						{
							Header: Strings.workshop.unit,
							id: "unit",
							width: 50,
							accessor: (row: any) => row.unit || "-"
						},
						{
							Header: Strings.workshop.preferredSupplier,
							id: "preferredSupplier",
							accessor: (row: any) => row.preferredSupplier?.name || "-"
						},
						{
							Header: Strings.workshop.averageCost,
							id: "averageCost",
							align: "right",
							accessor: (row: any) => (row.averageCost != null ? `${row.averageCost.toFixed(2)} €` : "-")
						}
					]}
					filterable
					isSinglePage
					fullPage
					paginated={false}
					add={{
						onClick: () => this.setState({ showDrawer: true, tempPart: {} })
					}}
					actions={{
						edit: (original: any) => ({
							onClick: () => this.props.dispatch(push(`/workshop/parts/${original._id}`))
						}),
						remove: (original: any) => ({
							onClick: () => this.removePart(original._id)
						}),
						toggle: (original: any) => ({
							value: original.isActive,
							onChange: () => this.togglePart(original)
						})
					}}
				/>
				{this.renderDrawer()}
			</React.Fragment>
		);
	}
}

const mapStateToProps = (state: any) => ({
	language: state.language
});

export default connect(mapStateToProps)(WorkshopParts);

